/**
 * Libro de Reclamaciones: registro contra la API de reclamaciones.
 *
 * Los name de los campos ya coinciden con CreateClaimDto, así que el payload se
 * arma casi directo; solo hay que normalizar booleanos, números y opcionales.
 * Si el formulario no lleva data-libro-endpoint, no hacemos nada: la sección
 * está en modo Shopify y el envío nativo debe seguir su curso.
 */
(function () {
  'use strict';

  var OPTIONAL_TEXT = ['itemDescription', 'orderNumber', 'guardianName'];

  function init(form) {
    var section = form.closest('[data-section-type="libro-reclamaciones"]');
    if (!section) return;

    // El editor de temas reinserta la sección con su <script>, así que el IIFE
    // se vuelve a ejecutar y además dispara shopify:section:load. Sin esta
    // marca acabaríamos con dos handlers de submit y dos reclamaciones
    // registradas, cada una con su propio correlativo.
    if (form.getAttribute('data-libro-ready') === '1') return;
    form.setAttribute('data-libro-ready', '1');

    var endpoint = form.getAttribute('data-libro-endpoint');
    var result = section.querySelector('[data-libro-result]');
    var errorBox = form.querySelector('[data-libro-error]');
    var submit = form.querySelector('[data-libro-submit]');
    var submitLabel = form.querySelector('[data-libro-submit-label]');
    var minorToggle = form.querySelector('[data-libro-minor]');
    var guardianWrap = form.querySelector('[data-libro-guardian]');
    var guardianInput = guardianWrap ? guardianWrap.querySelector('input') : null;
    var defaultLabel = submitLabel ? submitLabel.textContent : '';

    // El nombre del apoderado solo aplica (y solo es obligatorio) si es menor.
    function syncGuardian() {
      if (!minorToggle || !guardianWrap || !guardianInput) return;
      var isMinor = minorToggle.checked;
      guardianWrap.hidden = !isMinor;
      guardianInput.required = isMinor;
      if (!isMinor) guardianInput.value = '';
    }
    if (minorToggle) {
      minorToggle.addEventListener('change', syncGuardian);
      syncGuardian();
    }

    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = false;
      errorBox.classList.add('show');
      errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    function hideError() {
      if (!errorBox) return;
      errorBox.hidden = true;
      errorBox.classList.remove('show');
    }

    function setLoading(isLoading) {
      if (!submit) return;
      submit.disabled = isLoading;
      submit.classList.toggle('is-loading', isLoading);
      if (submitLabel) {
        submitLabel.textContent = isLoading ? 'Enviando...' : defaultLabel;
      }
    }

    /** Traduce los errores de class-validator del backend a un texto legible. */
    function readServerError(payload) {
      var message = payload && payload.message;
      if (Array.isArray(message)) return message.join('. ');
      if (typeof message === 'string') return message;
      return 'No pudimos registrar tu reclamación. Inténtalo nuevamente en unos minutos.';
    }

    function buildPayload(data) {
      var payload = {};

      data.forEach(function (value, key) {
        if (key === 'website' || key === 'consent') return;
        if (typeof value === 'string') value = value.trim();
        if (value !== '') payload[key] = value;
      });

      // isMinor llega como "true" solo si el checkbox está marcado.
      payload.isMinor = Boolean(minorToggle && minorToggle.checked);
      if (!payload.isMinor) delete payload.guardianName;

      // claimedAmount es @IsNumber en el DTO: enviamos number o nada.
      if (payload.claimedAmount !== undefined) {
        var amount = parseFloat(String(payload.claimedAmount).replace(',', '.'));
        if (isNaN(amount)) {
          delete payload.claimedAmount;
        } else {
          payload.claimedAmount = Math.round(amount * 100) / 100;
        }
      }

      // Los opcionales vacíos no deben viajar como cadena vacía.
      OPTIONAL_TEXT.forEach(function (key) {
        if (payload[key] === '') delete payload[key];
      });

      return payload;
    }

    form.addEventListener('submit', function (event) {
      // Trampa anti-spam: solo los bots rellenan este campo. Aplica en ambos modos.
      var trap = form.querySelector('#LibroWebsite');
      if (trap && trap.value !== '') {
        event.preventDefault();
        return;
      }

      // Sin endpoint la sección está en modo Shopify: dejamos correr el envío nativo.
      if (!endpoint) return;

      event.preventDefault();
      hideError();

      // El form es novalidate para controlar el mensaje, pero validamos igual.
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      var payload = buildPayload(new FormData(form));
      setLoading(true);

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              if (!response.ok) throw new Error(readServerError(body));
              return body;
            });
        })
        .then(function (entry) {
          if (!entry || !entry.claimCode) {
            throw new Error('La reclamación se registró pero no recibimos su código. Escríbenos para confirmarlo.');
          }
          showResult(entry);
        })
        .catch(function (error) {
          setLoading(false);
          showError(error.message || 'No pudimos conectar con el servidor. Inténtalo nuevamente.');
        });
    });

    function showResult(entry) {
      if (!result) return;

      var code = result.querySelector('[data-libro-code]');
      if (code) code.textContent = entry.claimCode;

      // El PDF cuelga del mismo recurso: /claims/:id/pdf?code=LR-YYYY-NNNNNN
      var pdfLink = result.querySelector('[data-libro-pdf]');
      if (pdfLink) {
        if (entry.id) {
          pdfLink.href =
            endpoint.replace(/\/+$/, '') +
            '/' +
            entry.id +
            '/pdf?code=' +
            encodeURIComponent(entry.claimCode);
        } else {
          pdfLink.hidden = true;
        }
      }

      form.hidden = true;
      result.hidden = false;
      result.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-libro-form]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  // El editor de temas re-renderiza la sección sin recargar la página.
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
